import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Activity, CheckCircle2, XCircle, Zap, Download, RefreshCw, Search,
  Filter, Calendar, ChevronLeft, ChevronRight, AlertTriangle, Clock,
  ArrowUp, ArrowDown, Hash, Layers, ArrowUpRight,
  Heart, Gauge, HardDrive, Trophy, Timer, TrendingUp, Cpu,
  Globe, ShieldCheck,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { EmptyState, Spinner } from '../components/Dialog';
import { AreaChart } from '../components/AreaChart';
import { apiFetch } from '../api';
import { formatNumber, formatRelative, copyToClipboard } from '../lib/utils';
import { cn } from '../lib/utils';

/**
 * UsagePage — Phase 3 redesign: "Logs & Analytics".
 *
 * Layout (top to bottom):
 *   1. Section header with title, description, and window-tab chips
 *   2. Four stat cards (Total / Successful / Failed / Credits used)
 *   3. Activity chart card with status-code filter chips
 *   4. Logs table with status filter + domain search + pagination
 *
 * Time-window tabs use shared highlight styling. Charts use the
 * aspect-ratio-aware AreaChart (no stretch).
 */

// Window definitions for the chip row
const WINDOWS = [
  { key: 'today',     label: 'Today',       days: 1 },
  { key: 'yesterday', label: 'Yesterday',   days: 1 },
  { key: '7d',        label: 'Last 7 days', days: 7 },
  { key: '15d',       label: 'Last 15 days',days: 15 },
  { key: '30d',       label: 'Last 30 days',days: 30 },
];

export default function UsagePage({ billing }) {
  const [window, setWindow] = useState('7d');

  // Three independent data sources
  const [summary, setSummary]   = useState(null);
  const [series, setSeries]     = useState([]);
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // Logs filters
  const [statusFilter, setStatusFilter] = useState('all');     // all | ok | error
  const [endpointFilter, setEndpointFilter] = useState('all'); // all | fetch | captcha
  const [domainQuery, setDomainQuery]   = useState('');
  const [page, setPage]                 = useState(0);
  const pageSize = 10;  // Phase-16: load less per page, page through faster

  async function load(signal) {
    setLoading(true); setError(null);
    try {
      const [s, ts, lg] = await Promise.all([
        apiFetch(`/v1/analytics/summary?window=${window}`),
        apiFetch(`/v1/logs/timeseries?window=${window}`),
        apiFetch(`/v1/logs?window=${window}&status_filter=${statusFilter === 'all' ? '' : statusFilter}&endpoint=${endpointFilter === 'all' ? '' : (endpointFilter === 'fetch' ? '/v1/fetch' : '/v1/captcha')}&limit=200`),
      ]);
      setSummary(s);
      setSeries(ts.series || []);
      setLogs(lg.logs || []);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [window, statusFilter]);

  // Filter logs by domain query + endpoint (client-side over the fetched page).
  const filteredLogs = useMemo(() => {
    let out = logs;
    if (endpointFilter === 'fetch') {
      out = out.filter((l) => l.endpoint === '/v1/fetch');
    } else if (endpointFilter === 'captcha') {
      out = out.filter((l) => l.endpoint === '/v1/captcha');
    }
    if (domainQuery) {
      const q = domainQuery.toLowerCase();
      out = out.filter((l) =>
        (l.domain && l.domain.toLowerCase().includes(q)) ||
        (l.endpoint && l.endpoint.toLowerCase().includes(q))
      );
    }
    return out;
  }, [logs, domainQuery, endpointFilter]);

  // Paginate
  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const visibleLogs = filteredLogs.slice(safePage * pageSize, (safePage + 1) * pageSize);

  // Computed chart data — convert ISO → short label
  const chartData = useMemo(() =>
    series.map((p) => ({
      label: formatBucketLabel(p.t, window),
      ok: p.ok,
      errors: p.errors,
      total: p.ok + p.errors,
    })),
    [series, window]
  );

  const totalChange = summary?.change_pct ?? 0;
  const failed = summary?.failed ?? 0;
  const total = summary?.total_calls ?? 0;

  // Per-endpoint breakdown from the current log page (so the user sees
  // a fetch vs captcha split at a glance, even when there's no /summary
  // per-endpoint data). For the current window.
  const endpointBreakdown = useMemo(() => {
    let fetchN = 0, captchaN = 0;
    for (const l of logs) {
      if (l.endpoint === '/v1/fetch') fetchN++;
      else if (l.endpoint === '/v1/captcha') captchaN++;
    }
    return { fetch: fetchN, captcha: captchaN, total: fetchN + captchaN };
  }, [logs]);

  function downloadCsv() {
    if (!filteredLogs.length) return;
    const headers = ['time','status','domain','proxy_mode','latency_ms','endpoint','request_id','error_kind','cost_credits'];
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filteredLogs.map((l) => [
      l.occurred_at, l.status, l.domain, l.proxy_mode, l.latency_ms, l.endpoint, l.request_id, l.error_kind, l.cost_credits
    ].map(escape).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `datames-logs-${window}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Logs & Analytics"
        description="Track your API consumption, success rate, and per-request history."
        actions={
          <Button variant="secondary" onClick={downloadCsv} disabled={!filteredLogs.length}>
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
        }
      />

      {/* Window tab row */}
      <WindowTabs value={window} onChange={setWindow} />

      {/* Endpoint breakdown — small bar that shows fetch vs captcha split.
          Driven by the current log page; updates with filters + window changes. */}
      {endpointBreakdown.total > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-2xs">
          <span className="text-fg-muted uppercase tracking-wider font-medium">Endpoint split:</span>
          <EndpointSplitChip
            kind="fetch"
            label="Fetch"
            count={endpointBreakdown.fetch}
            total={endpointBreakdown.total}
            icon={Globe}
          />
          <EndpointSplitChip
            kind="captcha"
            label="Captcha"
            count={endpointBreakdown.captcha}
            total={endpointBreakdown.total}
            icon={ShieldCheck}
          />
          <span className="text-fg-subtle">
            ({formatNumber(endpointBreakdown.total)} in this view)
          </span>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total calls"
          value={formatNumber(summary?.total_calls)}
          icon={Activity}
          accent="brand"
          changePct={totalChange}
          loading={loading}
        />
        <StatCard
          label="Successful"
          value={formatNumber(summary?.successful)}
          icon={CheckCircle2}
          accent="success"
          hint={total > 0 ? `${summary.success_rate}% success rate` : '—'}
          loading={loading}
        />
        <StatCard
          label="Failed"
          value={formatNumber(summary?.failed)}
          icon={XCircle}
          accent="destructive"
          hint={failed > 0 ? 'Investigate below' : 'Clean run'}
          loading={loading}
        />
        <StatCard
          label="Credits used"
          value={formatNumber(summary?.credits_used)}
          icon={Zap}
          accent="brand"
          hint="1 credit / successful call"
          loading={loading}
        />
      </div>

      {/* Activity (smaller, left) + API Health (new, right) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-3">
          <Card.Header>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Card.Title>Activity</Card.Title>
                <Badge variant="default">
                  <Layers className="h-3 w-3" />
                  {chartData.length} buckets
                </Badge>
              </div>
              <Card.Subtitle>
                {loading ? 'Loading…' :
                  `${formatNumber(series.reduce((s, p) => s + p.ok, 0))} successful · ${formatNumber(series.reduce((s, p) => s + p.errors, 0))} failed`}
              </Card.Subtitle>
            </div>
            <Button variant="ghost" size="icon-sm" onClick={() => load()} title="Refresh">
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </Button>
          </Card.Header>
          <Card.Body className="pt-2">
            <StatusChips series={series} />
            <div className="mt-4">
              <AreaChart
                data={chartData}
                height={200}
                series={[
                  { key: 'ok',     label: 'Successful', color: 'hsl(var(--success))' },
                  { key: 'errors', label: 'Failed',     color: 'hsl(var(--destructive))' },
                ]}
              />
            </div>
          </Card.Body>
        </Card>

        {/* API Health card — cozy companion to the chart */}
        <ApiHealthCard logs={logs} loading={loading} billing={billing} />
      </div>

      {/* Logs table */}
      <Card>
        <Card.Header>
          <div className="flex-1">
            <Card.Title>Request logs</Card.Title>
            <Card.Subtitle>
              Showing {visibleLogs.length} of {filteredLogs.length} entries in the selected window.
            </Card.Subtitle>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-2 p-4 border-b border-border">
            <div className="flex items-center gap-1 p-1 bg-surface-2 border border-border rounded-md">
              {[
                { k: 'all',   label: 'All',       icon: Layers },
                { k: 'ok',    label: 'Successful', icon: CheckCircle2 },
                { k: 'error', label: 'Failed',     icon: XCircle },
              ].map((opt) => {
                const active = statusFilter === opt.k;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.k}
                    onClick={() => { setStatusFilter(opt.k); setPage(0); }}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors',
                      active
                        ? 'bg-surface text-fg shadow-sm'
                        : 'text-fg-muted hover:text-fg'
                    )}
                  >
                    <Icon className={cn(
                      'h-3.5 w-3.5',
                      opt.k === 'ok' && 'text-success',
                      opt.k === 'error' && 'text-destructive',
                    )} />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {/* Endpoint type filter — Fetch vs Captcha */}
            <div className="flex items-center gap-1 p-1 bg-surface-2 border border-border rounded-md">
              {[
                { k: 'all',    label: 'All endpoints', icon: Layers },
                { k: 'fetch',  label: 'Fetch',         icon: Globe },
                { k: 'captcha',label: 'Captcha',       icon: ShieldCheck },
              ].map((opt) => {
                const active = endpointFilter === opt.k;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.k}
                    onClick={() => { setEndpointFilter(opt.k); setPage(0); }}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded transition-colors',
                      active
                        ? 'bg-surface text-fg shadow-sm'
                        : 'text-fg-muted hover:text-fg'
                    )}
                    title={`Filter to ${opt.label.toLowerCase()} calls only`}
                  >
                    <Icon className={cn(
                      'h-3.5 w-3.5',
                      opt.k === 'fetch' && 'text-success',
                      opt.k === 'captcha' && 'text-brand',
                    )} />
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="flex-1 min-w-[200px] max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
              <input
                type="text"
                placeholder="Search by domain or URL…"
                value={domainQuery}
                onChange={(e) => { setDomainQuery(e.target.value); setPage(0); }}
                className="w-full h-9 pl-9 pr-3 rounded-md bg-surface-2 border border-border text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand"
              />
            </div>

            <div className="ml-auto flex items-center gap-1">
              <Button variant="ghost" size="icon-sm" disabled={safePage === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-2xs text-fg-muted tabular-nums px-2">
                {safePage + 1} / {totalPages}
              </span>
              <Button variant="ghost" size="icon-sm" disabled={safePage >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Table */}
          {loading && logs.length === 0 ? (
            <div className="p-12 flex items-center justify-center"><Spinner size={20} /></div>
          ) : error ? (
            <div className="p-8">
              <EmptyState
                icon={AlertTriangle}
                title="Couldn't load logs"
                description={error}
                action={<Button variant="secondary" size="sm" onClick={() => load()}>Try again</Button>}
              />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={Activity}
                title={logs.length === 0
                  ? "No usage yet — make a call to see logs here"
                  : "No logs match the current filters"}
                description={logs.length === 0
                  ? "Hit POST /v1/fetch or POST /captcha with your API key, then refresh this page."
                  : "Try clearing the status or endpoint filters above, or pick a wider time window."}
                action={logs.length === 0
                  ? <Button variant="secondary" size="sm" onClick={() => load()}>
                      <RefreshCw className="h-3.5 w-3.5" /> Refresh
                    </Button>
                  : <Button variant="ghost" size="sm" onClick={() => { setStatusFilter('all'); setEndpointFilter('all'); setDomainQuery(''); setPage(0); }}>
                      Clear filters
                    </Button>}
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2/30">
                  <tr className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 w-12"></th>
                    <th className="text-left px-4 py-2.5">Time</th>
                    <th className="text-left px-4 py-2.5">Domain</th>
                    <th className="text-left px-4 py-2.5">Endpoint</th>
                    <th className="text-left px-4 py-2.5">Mode</th>
                    <th className="text-right px-4 py-2.5 w-24">Latency</th>
                    <th className="text-right px-4 py-2.5 w-20">Bytes</th>
        <th className="text-right px-4 py-2.5 w-20">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLogs.map((log) => (
                    <LogRow key={log.id || log.request_id} log={log} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

// =============================================================================
// Window tab row (time-range chips)
// =============================================================================
function WindowTabs({ value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-1 p-1 bg-surface border border-border rounded-lg w-fit">
      {WINDOWS.map((w) => {
        const active = value === w.key;
        return (
          <button
            key={w.key}
            type="button"
            onClick={() => onChange(w.key)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
              active
                ? 'bg-brand text-brand-fg shadow-sm'
                : 'text-fg-muted hover:text-fg hover:bg-surface-2'
            )}
          >
            {w.label}
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// Stat card — large number with accent-colored icon badge
// =============================================================================
function StatCard({ label, value, icon: Icon, accent, changePct, hint, loading }) {
  const toneMap = {
    brand:       'bg-brand-softest text-brand',
    success:     'bg-success-soft text-success-fg',
    destructive: 'bg-destructive-soft text-destructive',
  };
  const valueClass = {
    brand: 'text-fg',
    success: 'text-success-fg',
    destructive: failed => failed > 0 ? 'text-destructive' : 'text-fg',
  };

  return (
    <Card>
      <Card.Body className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-2xs font-medium text-fg-muted uppercase tracking-wider">
              {label}
            </div>
            <div className={cn(
              'mt-1 text-2xl font-semibold tabular-nums',
              accent === 'success' && 'text-success-fg',
              accent === 'destructive' && (typeof value === 'string' && parseInt(value.replace(/,/g,'')) > 0 ? 'text-destructive' : 'text-fg'),
            )}>
              {loading ? <span className="inline-block w-12 h-6 bg-surface-3 rounded animate-pulse" /> : value ?? '—'}
            </div>
            {hint && <div className="mt-1 text-2xs text-fg-subtle">{hint}</div>}
          </div>
          <div className={cn('h-9 w-9 rounded-md flex items-center justify-center shrink-0', toneMap[accent])}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        {changePct !== undefined && changePct !== null && (
          <div className="mt-3 pt-3 border-t border-border flex items-center gap-1.5 text-2xs">
            {changePct === 0 ? (
              <span className="text-fg-muted">— No change vs previous</span>
            ) : changePct > 0 ? (
              <span className="inline-flex items-center gap-0.5 text-success-fg">
                <ArrowUp className="h-3 w-3" /> +{changePct}% vs previous period
              </span>
            ) : (
              <span className="inline-flex items-center gap-0.5 text-destructive">
                <ArrowDown className="h-3 w-3" /> {changePct}% vs previous period
              </span>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

// =============================================================================
// Status chips for the chart legend (clickable, would filter below)
// =============================================================================
function StatusChips({ series }) {
  const totals = useMemo(() => ({
    ok:     series.reduce((s, p) => s + (p.ok || 0), 0),
    errors: series.reduce((s, p) => s + (p.errors || 0), 0),
  }), [series]);
  const total = totals.ok + totals.errors;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <LegendChip color="hsl(var(--success))" label="Successful" value={totals.ok} pct={pct(totals.ok, total)} />
      <LegendChip color="hsl(var(--destructive))" label="Failed" value={totals.errors} pct={pct(totals.errors, total)} />
    </div>
  );
}

function LegendChip({ color, label, value, pct }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-surface-2 border border-border text-2xs">
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-fg-muted">{label}</span>
      <span className="text-fg font-semibold tabular-nums">{formatNumber(value)}</span>
      <span className="text-fg-subtle">({pct}%)</span>
    </div>
  );
}

// =============================================================================
// One row in the logs table
// =============================================================================
function LogRow({ log }) {
  const ok = log.status === 'ok';
  const [copied, setCopied] = useState(false);
  // Classify: /v1/fetch → Fetch, /v1/captcha → Captcha, anything else → raw path
  const isCaptcha = log.endpoint === '/v1/captcha';
  const isFetch = log.endpoint === '/v1/fetch';
  const TypeIcon = isCaptcha ? ShieldCheck : isFetch ? Globe : null;

  async function copy() {
    const ok = await copyToClipboard(log.endpoint || log.request_id || '');
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-surface-2/30 transition-colors">
      <td className="px-4 py-2.5">
        {ok
          ? <Badge variant="success"><Dot variant="success" />2xx</Badge>
          : <Badge variant="destructive"><Dot variant="destructive" />5xx</Badge>}
      </td>
      <td className="px-4 py-2.5 text-2xs text-fg-muted whitespace-nowrap tabular-nums">
        {log.occurred_at ? new Date(log.occurred_at).toLocaleString(undefined, {
          month: 'short', day: 'numeric',
          hour: '2-digit', minute: '2-digit', second: '2-digit',
        }) : '—'}
      </td>
      <td className="px-4 py-2.5 text-xs text-fg">
        {log.domain || <span className="text-fg-subtle italic">—</span>}
      </td>
      <td className="px-4 py-2.5 max-w-md">
        {isCaptcha || isFetch ? (
          <span className={cn(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-semibold uppercase tracking-wider',
            isCaptcha
              ? 'bg-brand-softest text-brand-strong'
              : 'bg-success-soft text-success-fg',
          )}>
            {TypeIcon && <TypeIcon className="h-2.5 w-2.5" />}
            {isCaptcha ? 'Captcha' : 'Fetch'}
          </span>
        ) : (
          <span className="text-2xs text-fg-muted">—</span>
        )}
        <div className="mt-1 flex items-center gap-1 group">
          <code className="font-mono text-2xs text-fg-subtle truncate" title={log.endpoint}>
            {log.endpoint || '—'}
          </code>
          {log.endpoint && (
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center justify-center h-4 w-4 rounded text-fg-muted hover:text-fg transition-colors opacity-0 group-hover:opacity-100 shrink-0"
              title="Copy endpoint path"
            >
              {copied
                ? <CheckCircle2 className="h-2.5 w-2.5 text-success" />
                : <span className="text-2xs">copy</span>}
            </button>
          )}
        </div>
        {log.error_kind && (
          <div className="mt-0.5 text-2xs text-destructive truncate">{log.error_kind}</div>
        )}
      </td>
      <td className="px-4 py-2.5">
        {log.proxy_mode
          ? <Badge variant="default">{log.proxy_mode}</Badge>
          : <span className="text-fg-subtle">—</span>}
      </td>
      <td className="px-4 py-2.5 text-2xs text-right tabular-nums text-fg-muted">
        {log.latency_ms != null ? `${formatNumber(log.latency_ms)} ms` : '—'}
      </td>
      <td className="px-4 py-2.5 text-2xs text-right tabular-nums text-fg-muted">
        {log.bytes_out != null ? formatBytes(log.bytes_out) : '—'}
      </td>
      <td className="px-4 py-2.5 text-2xs text-right tabular-nums">
        {log.cost_credits != null ? (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 font-medium',
              log.cost_credits >= 4 ? 'text-warning-fg' : log.cost_credits > 1 ? 'text-brand' : 'text-fg-muted'
            )}
            title={
              log.cost_credits === 1 ? '1 credit (classic)' :
              log.cost_credits === 4 ? '4 credits (Datames/stealth)' :
              `${log.cost_credits} credits`
            }
          >
            <Zap className="h-2.5 w-2.5" />
            {log.cost_credits}
          </span>
        ) : '—'}
      </td>
    </tr>
  );
}

function pct(part, whole) {
  if (!whole) return '0';
  return Math.round((part / whole) * 100).toString();
}

function formatBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatBucketLabel(iso, windowKey) {
  if (!iso) return '';
  const d = new Date(iso);
  if (windowKey === 'today' || windowKey === 'yesterday') {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Local AreaChart component (not imported) — to avoid changing global component.
// Wraps the existing one to enforce the right height/ratio for this page.

// =============================================================================
// API Health card — cozy companion to the Activity chart.
// Sits in the right column of the 5-col grid.
//
// Stats shown (computed from the logs in the selected window):
//   - Credits spent      — sum of credits_used / 1
//   - Avg response time  — mean of latency_ms (successful only)
//   - p95 response time  — 95th percentile latency
//   - Total data out     — sum of bytes_out
//   - Most hit domain    — top domain by request count
//   - Fastest response   — min latency_ms
// =============================================================================
function ApiHealthCard({ logs, loading, billing }) {
  const stats = useMemo(() => computeHealthStats(logs, billing), [logs, billing]);
  const healthScore = useMemo(() => computeHealthScore(stats), [stats]);

  return (
    <Card className="lg:col-span-2 bg-gradient-to-br from-surface to-surface-2/40">
      <Card.Header>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Card.Title>API Health</Card.Title>
            <Badge variant={healthScore.tone}>
              <Heart className="h-3 w-3" />
              {healthScore.label}
            </Badge>
          </div>
          <Card.Subtitle>
            {stats.requestCount > 0
              ? `Health snapshot · ${formatNumber(stats.requestCount)} requests in window`
              : 'No requests in this window yet'}
          </Card.Subtitle>
        </div>
      </Card.Header>
      <Card.Body className="pt-2">
        {loading && !logs.length ? (
          <div className="p-8 flex items-center justify-center">
            <Spinner size={20} />
          </div>
        ) : (
          <div className="space-y-2.5">
            {/* Hero: credits spent */}
            <HeroStat
              icon={Zap}
              label="Credits spent"
              value={stats.creditsSpent}
              suffix="credits"
              tone="brand"
              sub={stats.requestCount > 0
                ? `${formatNumber(stats.requestCount)} requests · ${stats.creditsSpent > 0 ? (stats.creditsSpent / Math.max(stats.requestCount, 1)).toFixed(1) : 0} avg cost`
                : 'No usage yet'}
            />

            {/* Avg + p95 latency side by side */}
            <div className="grid grid-cols-2 gap-2.5">
              <MiniStat
                icon={Gauge}
                label="Avg latency"
                value={stats.avgLatency}
                suffix="ms"
                tone="brand"
                pulse
              />
              <MiniStat
                icon={Timer}
                label="p95 latency"
                value={stats.p95Latency}
                suffix="ms"
                tone="warning"
              />
            </div>

            {/* Data + top domain */}
            <div className="grid grid-cols-2 gap-2.5">
              <MiniStat
                icon={HardDrive}
                label="Data out"
                value={stats.totalBytesLabel}
                tone="default"
              />
              <MiniStat
                icon={Trophy}
                label="Top domain"
                value={stats.topDomain}
                isText
                tone="default"
              />
            </div>

            {/* Speed badge row */}
            <div className="pt-2 mt-1 border-t border-border grid grid-cols-3 gap-2 text-center">
              <SpeedPill
                icon={TrendingUp}
                label="Fastest"
                value={stats.fastestLatency != null ? `${stats.fastestLatency} ms` : '—'}
                tone="success"
              />
              <SpeedPill
                icon={Cpu}
                label="Median"
                value={stats.medianLatency != null ? `${stats.medianLatency} ms` : '—'}
                tone="default"
              />
              <SpeedPill
                icon={AlertTriangle}
                label="Slowest"
                value={stats.slowestLatency != null ? `${stats.slowestLatency} ms` : '—'}
                tone="destructive"
              />
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

function HeroStat({ icon: Icon, label, value, suffix, tone, sub }) {
  const toneMap = {
    brand:       'bg-brand-softest text-brand',
    success:     'bg-success-soft text-success-fg',
    destructive: 'bg-destructive-soft text-destructive',
    warning:     'bg-warning-soft text-warning',
    default:     'bg-surface-2 text-fg-muted',
  };
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-2/60 border border-border">
      <div className={cn('h-10 w-10 rounded-md flex items-center justify-center shrink-0', toneMap[tone])}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-2xs uppercase tracking-wider text-fg-muted">{label}</div>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className="text-xl font-semibold text-fg tabular-nums">
            {value !== null && value !== undefined ? formatNumber(value) : '—'}
          </span>
          {suffix && <span className="text-2xs text-fg-muted">{suffix}</span>}
        </div>
        {sub && <div className="text-2xs text-fg-subtle mt-0.5 truncate">{sub}</div>}
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, suffix, tone = 'default', isText, pulse }) {
  const toneMap = {
    brand:       'bg-brand-softest text-brand',
    success:     'bg-success-soft text-success-fg',
    destructive: 'bg-destructive-soft text-destructive',
    warning:     'bg-warning-soft text-warning',
    default:     'bg-surface-2 text-fg-muted',
  };
  return (
    <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-surface-2/40 border border-border">
      <div className={cn('h-7 w-7 rounded-md flex items-center justify-center shrink-0', toneMap[tone])}>
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-2xs uppercase tracking-wider text-fg-muted truncate">{label}</div>
        <div className="flex items-baseline gap-1 mt-0.5">
          {isText ? (
            <span className="text-sm font-medium text-fg truncate" title={value || '—'}>
              {value || '—'}
            </span>
          ) : (
            <>
              <span className="text-sm font-semibold text-fg tabular-nums">
                {value !== null && value !== undefined ? formatNumber(value) : '—'}
              </span>
              {suffix && <span className="text-2xs text-fg-muted">{suffix}</span>}
              {pulse && value !== null && (
                <span className="ml-auto h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SpeedPill({ icon: Icon, label, value, tone = 'default' }) {
  const toneMap = {
    success:     'text-success-fg',
    destructive: 'text-destructive',
    default:     'text-fg-muted',
  };
  const iconMap = {
    success:     'bg-success-soft text-success-fg',
    destructive: 'bg-destructive-soft text-destructive',
    default:     'bg-surface-2 text-fg-muted',
  };
  return (
    <div>
      <div className={cn('h-6 w-6 rounded-full mx-auto flex items-center justify-center mb-1', iconMap[tone])}>
        <Icon className="h-3 w-3" />
      </div>
      <div className="text-2xs text-fg-subtle uppercase tracking-wider">{label}</div>
      <div className={cn('text-xs font-semibold tabular-nums mt-0.5', toneMap[tone])}>{value}</div>
    </div>
  );
}

// =============================================================================
// Health stat computation — pure functions
// =============================================================================
function computeHealthStats(logs, billing) {
  const out = {
    requestCount: 0,
    creditsSpent: 0,
    avgLatency: null,
    medianLatency: null,
    p95Latency: null,
    fastestLatency: null,
    slowestLatency: null,
    totalBytes: 0,
    totalBytesLabel: '—',
    topDomain: null,
  };

  if (!logs || !logs.length) return out;

  // Only successful requests contribute to "credits spent" + latency stats
  const ok = logs.filter((l) => l.status === 'ok');
  out.requestCount = ok.length;
  // Phase-8: sum the actual cost_credits column (variable: 1 classic, 4 stealth, +extras).
  out.creditsSpent = ok.reduce((s, l) => s + (l.cost_credits || 0), 0);

  const latencies = ok.map((l) => l.latency_ms).filter((n) => Number.isFinite(n));
  if (latencies.length) {
    latencies.sort((a, b) => a - b);
    out.fastestLatency = latencies[0];
    out.slowestLatency = latencies[latencies.length - 1];
    out.medianLatency = latencies[Math.floor(latencies.length / 2)];
    const sum = latencies.reduce((s, n) => s + n, 0);
    out.avgLatency = Math.round(sum / latencies.length);
    const p95Idx = Math.min(latencies.length - 1, Math.floor(latencies.length * 0.95));
    out.p95Latency = latencies[p95Idx];
  }

  out.totalBytes = logs.reduce((s, l) => s + (l.bytes_out || 0), 0);
  out.totalBytesLabel = formatBytesCompact(out.totalBytes);

  // Most-hit domain
  const counts = new Map();
  for (const l of logs) {
    if (!l.domain) continue;
    counts.set(l.domain, (counts.get(l.domain) || 0) + 1);
  }
  if (counts.size) {
    let best = null;
    let bestN = -1;
    for (const [domain, n] of counts) {
      if (n > bestN) { best = domain; bestN = n; }
    }
    out.topDomain = best;
  }

  return out;
}

function computeHealthScore(stats) {
  // 100 if no requests yet
  if (!stats.requestCount) {
    return { label: 'No data', tone: 'default', score: null };
  }
  const score = computeScore(stats);
  if (score >= 90) return { label: 'Excellent', tone: 'success', score };
  if (score >= 75) return { label: 'Good',      tone: 'success', score };
  if (score >= 60) return { label: 'Fair',      tone: 'warning', score };
  return { label: 'Needs attention', tone: 'destructive', score };
}

// Score formula: penalized by avg latency across the window.
// Thresholds (in ms, per spec):
//   < 10,000 ms  → 100 (Excellent)
//   < 25,000 ms  → 80  (Good)
//   < 60,000 ms  → 65  (Fair)
//   >= 60,000 ms → below 60 → Needs attention
function computeScore(stats) {
  let s = 100;
  if (stats.avgLatency != null) {
    if (stats.avgLatency >= 60000)      s = 50;   // Needs attention
    else if (stats.avgLatency >= 25000) s = 65;   // Fair
    else if (stats.avgLatency >= 10000) s = 80;   // Good
    else                                s = 100;  // Excellent
  }
  if (stats.p95Latency != null && stats.p95Latency > 8000) s -= 15;
  return Math.max(0, Math.min(100, s));
}

function formatBytesCompact(b) {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// Compact chip used in the "Endpoint split" row above the stat cards.
// Shows icon + label + count + a thin bar of the share.
function EndpointSplitChip({ kind, label, count, total, icon: Icon }) {
  const tone = kind === 'fetch'
    ? 'bg-success-soft text-success-fg'
    : 'bg-brand-softest text-brand-strong';
  const bar = kind === 'fetch' ? 'bg-success' : 'bg-brand';
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border text-2xs', tone)}>
      <Icon className="h-3 w-3" />
      <span className="font-semibold tabular-nums">{label}</span>
      <span className="tabular-nums">{formatNumber(count)}</span>
      <span className="opacity-60">({pct}%)</span>
      <span className="hidden sm:inline-block w-12 h-1 rounded-full bg-surface-3 overflow-hidden ml-1">
        <span className={cn('block h-full', bar)} style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
}